import React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useDragDropManager } from 'react-dnd';
import { commitEditorDrop } from './commit-editor-drop';
import {
  pickBestDropSlot,
  type DropSlotCandidate,
} from '../../domain/dnd/collision';
import type { DragData, DropData } from '../../domain/dnd/types';
import type { DocumentSection } from '../../domain/types';
import { isDropSlotEnabled } from '../../domain/dnd/rules';
import { getEditorStoreState } from '../../domain/store';
import { applyEditorDragVerticalAutoScroll } from './editor-drag-auto-scroll';
import {
  getEditorDragItemSnapshot,
  publishEditorDragItem,
  subscribeEditorDragItem,
} from './editor-drag-item-store';
import { isPointerInEmailBuilderCanvasDropZone } from './pointer-guard';

export type RegisteredDropSlot = {
  id: string;
  data: DropData;
  getRect: () => DOMRect | undefined;
};

export type RegisteredHoverTarget =
  | {
      id: string;
      kind: 'block';
      rowId: string;
      columnId: string;
      blockId: string;
      index: number;
      getRect: () => DOMRect | undefined;
    }
  | {
      id: string;
      kind: 'row';
      rowId: string;
      getRect: () => DOMRect | undefined;
    }
  | {
      id: string;
      kind: 'group';
      groupId: string;
      sectionIndex: number;
      getRect: () => DOMRect | undefined;
    }
  | {
      id: string;
      kind: 'column';
      rowId: string;
      columnId: string;
      getRect: () => DOMRect | undefined;
    };

function hoverTargetRegistrationKey(target: RegisteredHoverTarget | null): string | null {
  if (target === null) {
    return null;
  }

  switch (target.kind) {
    case 'block':
      return `block:${target.id}:${target.rowId}:${target.columnId}:${target.blockId}:${target.index}`;
    case 'row':
      return `row:${target.id}:${target.rowId}`;
    case 'group':
      return `group:${target.id}:${target.groupId}:${target.sectionIndex}`;
    case 'column':
      return `column:${target.id}:${target.rowId}:${target.columnId}`;
  }
}

function toDropSlotRect(rect: DOMRect): DropSlotCandidate['rect'] {
  return {
    left: rect.left,
    right: rect.right,
    top: rect.top,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height,
  };
}

function buildHoverContext(
  targets: Map<string, RegisteredHoverTarget>,
  sections: DocumentSection[],
) {
  const blockHoverTargets = [];
  const columnHoverTargets = [];
  const rowHoverTargets = [];
  const groupHoverTargets = [];

  for (const target of targets.values()) {
    const rect = target.getRect();
    if (!rect) {
      continue;
    }

    const slotRect = toDropSlotRect(rect);
    if (target.kind === 'block') {
      blockHoverTargets.push({
        rowId: target.rowId,
        columnId: target.columnId,
        blockId: target.blockId,
        index: target.index,
        rect: slotRect,
      });
      continue;
    }

    if (target.kind === 'column') {
      columnHoverTargets.push({
        rowId: target.rowId,
        columnId: target.columnId,
        rect: slotRect,
      });
      continue;
    }

    if (target.kind === 'row') {
      rowHoverTargets.push({
        rowId: target.rowId,
        rect: slotRect,
      });
      continue;
    }

    groupHoverTargets.push({
      groupId: target.groupId,
      sectionIndex: target.sectionIndex,
      rect: slotRect,
    });
  }

  return { sections, blockHoverTargets, columnHoverTargets, rowHoverTargets, groupHoverTargets };
}

type DropSlotRect = DropSlotCandidate['rect'];

function dropSlotRectsEqual(
  left: DropSlotRect,
  right: DropSlotRect,
): boolean {
  return (
    left.left === right.left &&
    left.top === right.top &&
    left.width === right.width &&
    left.height === right.height
  );
}

type DropSlotRegistrationValue = {
  register: (slot: RegisteredDropSlot) => void;
  unregister: (id: string) => void;
};

const DropSlotRegistrationContext = createContext<DropSlotRegistrationValue | null>(
  null,
);
const CollisionSlotIdContext = createContext<string | null>(null);
const ActiveDropDataContext = createContext<DropData | null>(null);
const CollisionRectContext = createContext<DropSlotRect | null>(null);

function resolveBestDropSlot(
  pointer: { x: number; y: number },
  drag: DragData,
  slots: Map<string, RegisteredDropSlot>,
  hoverTargets: Map<string, RegisteredHoverTarget>,
): DropSlotCandidate | undefined {
  const sections = getEditorStoreState().document.sections;
  const candidates: DropSlotCandidate[] = [];

  for (const slot of slots.values()) {
    const rect = slot.getRect();
    if (!rect) {
      continue;
    }
    const candidate: DropSlotCandidate = {
      id: slot.id,
      data: slot.data,
      rect: toDropSlotRect(rect),
    };
    if (isDropSlotEnabled(sections, candidate.data, drag)) {
      candidates.push(candidate);
    }
  }

  return pickBestDropSlot(
    pointer,
    candidates,
    drag,
    buildHoverContext(hoverTargets, sections),
  );
}

function EditorCollisionDropListener(): null {
  const manager = useDragDropManager();
  const {
    slotsRef,
    hoverTargetsRef,
    applyCollision,
    clearCollision,
  } = useDropSlotRegistryInternal();
  const wasDraggingRef = useRef(false);
  const dropCommittedRef = useRef(false);
  const dragCancelledRef = useRef(false);
  const nativeDropEffectRef = useRef<string | null>(null);
  const nativeDragEndReceivedRef = useRef(false);
  const awaitingDragEndFinalizeRef = useRef(false);
  const pointerReleasedDuringDragRef = useRef(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const lastItemRef = useRef<DragData | null>(null);
  const collisionRafRef = useRef<number | null>(null);

  const finalizeDragCommit = useCallback(() => {
    if (!awaitingDragEndFinalizeRef.current) {
      return;
    }
    awaitingDragEndFinalizeRef.current = false;

    const dropEffect = nativeDropEffectRef.current;
    const escapeCancelled =
      dropEffect === 'none' && !pointerReleasedDuringDragRef.current;
    const cancelled = dragCancelledRef.current || escapeCancelled;
    dragCancelledRef.current = false;
    pointerReleasedDuringDragRef.current = false;

    const item = lastItemRef.current;
    const offset = lastPointerRef.current;

    if (!cancelled && item && offset && isPointerInEmailBuilderCanvasDropZone(offset)) {
      const best = resolveBestDropSlot(
        offset,
        item,
        slotsRef.current,
        hoverTargetsRef.current,
      );
      if (!dropCommittedRef.current && best) {
        dropCommittedRef.current = true;
        commitEditorDrop(item, best.data);
      }
    }

    clearCollision();
    lastItemRef.current = null;
    lastPointerRef.current = null;
    nativeDropEffectRef.current = null;
    nativeDragEndReceivedRef.current = false;
  }, [clearCollision, slotsRef, hoverTargetsRef]);

  const syncCollisionAtPointer = useCallback(
    (pointer: { x: number; y: number }) => {
      const monitor = manager.getMonitor();
      if (!monitor.isDragging()) {
        return;
      }
      const item = monitor.getItem() as DragData | null;
      if (!item) {
        return;
      }
      lastItemRef.current = item;
      lastPointerRef.current = pointer;
      if (!isPointerInEmailBuilderCanvasDropZone(pointer)) {
        clearCollision();
        return;
      }
      const best = resolveBestDropSlot(
        pointer,
        item,
        slotsRef.current,
        hoverTargetsRef.current,
      );
      applyCollision(best);
    },
    [manager, applyCollision, clearCollision, slotsRef, hoverTargetsRef],
  );

  const scheduleCollisionSync = useCallback(
    (pointer: { x: number; y: number }) => {
      lastPointerRef.current = pointer;
      if (collisionRafRef.current !== null) {
        return;
      }
      collisionRafRef.current = requestAnimationFrame(() => {
        collisionRafRef.current = null;
        const scheduledPointer = lastPointerRef.current;
        if (scheduledPointer) {
          syncCollisionAtPointer(scheduledPointer);
        }
      });
    },
    [syncCollisionAtPointer],
  );

  useEffect(() => {
    const monitor = manager.getMonitor();
    let autoScrollFrameId: number | null = null;

    const stopAutoScroll = () => {
      if (autoScrollFrameId !== null) {
        cancelAnimationFrame(autoScrollFrameId);
        autoScrollFrameId = null;
      }
    };

    const runAutoScrollFrame = () => {
      autoScrollFrameId = null;
      if (!monitor.isDragging()) {
        return;
      }

      const pointer =
        lastPointerRef.current ?? monitor.getClientOffset() ?? null;
      if (pointer && applyEditorDragVerticalAutoScroll(pointer)) {
        syncCollisionAtPointer(pointer);
      }

      if (monitor.isDragging()) {
        autoScrollFrameId = requestAnimationFrame(runAutoScrollFrame);
      }
    };

    const startAutoScroll = () => {
      if (autoScrollFrameId === null) {
        autoScrollFrameId = requestAnimationFrame(runAutoScrollFrame);
      }
    };

    const onOffsetChange = () => {
      const offset = monitor.getClientOffset();
      if (offset) {
        scheduleCollisionSync(offset);
      }
    };

    const onDragOver = (event: DragEvent) => {
      if (monitor.isDragging()) {
        scheduleCollisionSync({ x: event.clientX, y: event.clientY });
      }
    };

    const onMouseUp = () => {
      if (monitor.isDragging()) {
        pointerReleasedDuringDragRef.current = true;
      }
    };

    const onDragEnd = (event: DragEvent) => {
      nativeDragEndReceivedRef.current = true;
      nativeDropEffectRef.current = event.dataTransfer?.dropEffect ?? 'none';
      if (awaitingDragEndFinalizeRef.current) {
        finalizeDragCommit();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !monitor.isDragging()) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragCancelledRef.current = true;
      clearCollision();
      manager.getActions().endDrag();
    };

    const unsubOffset = monitor.subscribeToOffsetChange(onOffsetChange);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('mouseup', onMouseUp, true);
    window.addEventListener('dragend', onDragEnd, true);
    window.addEventListener('keydown', onKeyDown, true);

    const unsubState = monitor.subscribeToStateChange(() => {
      const dragging = monitor.isDragging();

      if (dragging) {
        if (!wasDraggingRef.current) {
          dropCommittedRef.current = false;
          dragCancelledRef.current = false;
          nativeDropEffectRef.current = null;
          nativeDragEndReceivedRef.current = false;
          awaitingDragEndFinalizeRef.current = false;
          pointerReleasedDuringDragRef.current = false;
          startAutoScroll();
          // Publish via external store after dragstart settles. React state on
          // this provider re-renders the canvas and Chrome aborts HTML5 drag.
          window.setTimeout(() => {
            if (!monitor.isDragging()) {
              return;
            }
            publishEditorDragItem((monitor.getItem() as DragData | null) ?? null);
            const deferredOffset = monitor.getClientOffset();
            if (deferredOffset) {
              scheduleCollisionSync(deferredOffset);
            }
          }, 0);
        } else {
          publishEditorDragItem((monitor.getItem() as DragData | null) ?? null);
          const offset = monitor.getClientOffset();
          if (offset) {
            scheduleCollisionSync(offset);
          }
        }
      } else if (wasDraggingRef.current) {
        publishEditorDragItem(null);
        stopAutoScroll();
        awaitingDragEndFinalizeRef.current = true;
        clearCollision();
        if (nativeDragEndReceivedRef.current) {
          finalizeDragCommit();
        }
      }

      wasDraggingRef.current = dragging;
    });

    return () => {
      stopAutoScroll();
      if (collisionRafRef.current !== null) {
        cancelAnimationFrame(collisionRafRef.current);
        collisionRafRef.current = null;
      }
      unsubOffset();
      unsubState();
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('mouseup', onMouseUp, true);
      window.removeEventListener('dragend', onDragEnd, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [manager, clearCollision, slotsRef, hoverTargetsRef, scheduleCollisionSync, syncCollisionAtPointer, finalizeDragCommit]);

  return null;
}

type RegistryInternal = {
  slotsRef: MutableRefObject<Map<string, RegisteredDropSlot>>;
  hoverTargetsRef: MutableRefObject<Map<string, RegisteredHoverTarget>>;
  applyCollision: (best: DropSlotCandidate | undefined) => void;
  clearCollision: () => void;
  registerHoverTarget: (target: RegisteredHoverTarget) => void;
  unregisterHoverTarget: (id: string) => void;
};

const DropSlotRegistryInternalContext = createContext<RegistryInternal | null>(
  null,
);

function useDropSlotRegistryInternal(): RegistryInternal {
  const value = useContext(DropSlotRegistryInternalContext);
  if (!value) {
    throw new Error(
      'useDropSlotRegistryInternal must be used within DropSlotRegistryProvider',
    );
  }
  return value;
}

export function DropSlotRegistryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const slotsRef = useRef<Map<string, RegisteredDropSlot>>(new Map());
  const hoverTargetsRef = useRef<Map<string, RegisteredHoverTarget>>(new Map());
  const [collisionSlotId, setCollisionSlotId] = useState<string | null>(null);
  const [activeDropData, setActiveDropData] = useState<DropData | null>(null);
  const [collisionRect, setCollisionRect] = useState<DropSlotRect | null>(null);
  const collisionSlotIdRef = useRef<string | null>(null);
  const activeDropDataRef = useRef<DropData | null>(null);
  const collisionRectRef = useRef<DropSlotRect | null>(null);

  const register = useCallback((slot: RegisteredDropSlot) => {
    slotsRef.current.set(slot.id, slot);
  }, []);

  const unregister = useCallback((id: string) => {
    slotsRef.current.delete(id);
  }, []);

  const registrationValue = useMemo(
    () => ({
      register,
      unregister,
    }),
    [register, unregister],
  );

  const applyCollision = useCallback((best: DropSlotCandidate | undefined) => {
    const nextId = best?.id ?? null;
    const nextData = best?.data ?? null;
    const nextRect = best?.rect ?? null;

    if (collisionSlotIdRef.current !== nextId) {
      collisionSlotIdRef.current = nextId;
      setCollisionSlotId(nextId);
      if (activeDropDataRef.current !== nextData) {
        activeDropDataRef.current = nextData;
        setActiveDropData(nextData);
      }
    }

    if (nextRect === null) {
      if (collisionRectRef.current !== null) {
        collisionRectRef.current = null;
        setCollisionRect(null);
      }
    } else if (
      collisionRectRef.current === null ||
      !dropSlotRectsEqual(collisionRectRef.current, nextRect)
    ) {
      collisionRectRef.current = nextRect;
      setCollisionRect(nextRect);
    }
  }, []);

  const clearCollision = useCallback(() => {
    if (collisionSlotIdRef.current !== null) {
      collisionSlotIdRef.current = null;
      setCollisionSlotId(null);
    }
    if (activeDropDataRef.current !== null) {
      activeDropDataRef.current = null;
      setActiveDropData(null);
    }
    if (collisionRectRef.current !== null) {
      collisionRectRef.current = null;
      setCollisionRect(null);
    }
  }, []);

  const registerHoverTarget = useCallback((target: RegisteredHoverTarget) => {
    hoverTargetsRef.current.set(target.id, target);
  }, []);

  const unregisterHoverTarget = useCallback((id: string) => {
    hoverTargetsRef.current.delete(id);
  }, []);

  const internalValue = useMemo(
    () => ({
      slotsRef,
      hoverTargetsRef,
      applyCollision,
      clearCollision,
      registerHoverTarget,
      unregisterHoverTarget,
    }),
    [
      applyCollision,
      clearCollision,
      registerHoverTarget,
      unregisterHoverTarget,
    ],
  );

  return (
    <DropSlotRegistryInternalContext.Provider value={internalValue}>
      <DropSlotRegistrationContext.Provider value={registrationValue}>
        <CollisionSlotIdContext.Provider value={collisionSlotId}>
          <ActiveDropDataContext.Provider value={activeDropData}>
            <CollisionRectContext.Provider value={collisionRect}>
              <EditorCollisionDropListener />
              {children}
            </CollisionRectContext.Provider>
          </ActiveDropDataContext.Provider>
        </CollisionSlotIdContext.Provider>
      </DropSlotRegistrationContext.Provider>
    </DropSlotRegistryInternalContext.Provider>
  );
}

export function useCollisionSlotId(): string | null {
  return useContext(CollisionSlotIdContext);
}

export function useActiveDropData(): DropData | null {
  return useContext(ActiveDropDataContext);
}

export function useCollisionRect(): DropSlotRect | null {
  return useContext(CollisionRectContext);
}

export function useEditorDragItem(): DragData | null {
  const [item, setItem] = useState<DragData | null>(getEditorDragItemSnapshot);
  useEffect(() => subscribeEditorDragItem(() => {
    setItem(getEditorDragItemSnapshot());
  }), []);
  return item;
}

export function useRegisterDropSlot(slot: RegisteredDropSlot): void {
  const registration = useContext(DropSlotRegistrationContext);
  if (!registration) {
    throw new Error(
      'useRegisterDropSlot must be used within DropSlotRegistryProvider',
    );
  }
  const { register, unregister } = registration;
  const { id, data, getRect } = slot;

  useEffect(() => {
    register({ id, data, getRect });
    return () => unregister(id);
  }, [register, unregister, id, data, getRect]);
}

export function useRegisterHoverTarget(
  target: RegisteredHoverTarget | null,
): void {
  const { registerHoverTarget, unregisterHoverTarget } =
    useDropSlotRegistryInternal();
  const registrationKey = hoverTargetRegistrationKey(target);

  useEffect(() => {
    if (!target) {
      return;
    }

    registerHoverTarget(target);
    return () => unregisterHoverTarget(target.id);
  }, [registrationKey, registerHoverTarget, target, unregisterHoverTarget]);
}
